import {
  DerivedDefinitionType,
  IDerivedColumnDraft,
} from '../../../../redux/slices/dataModeler';
import React, { useCallback, useState } from 'react';
import { UnderTableHeaderDiv } from '../DataModelerCommonUI';
import InformationalTooltip from '../../../../components/InformationalTooltip';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import {
  CustomColumnCell,
  DerivedColumnDraftFormWrapper,
  DerivedColumnSelectRow,
  StyledDefinitionSelectMenu,
  StyledFieldNameInput,
  StyledTypeSelectMenu,
} from './DerivedColumnEditUI';
import { FieldError, FieldLabel } from '../../../../components/Form';
import { FIELD_TYPE_OPTIONS, fieldTypeName, IPreviewDataRows } from '../utils';
import {
  FieldType,
  IFieldTypeParamsFieldsFragment,
  IMappedStringDefinitionInput,
  IRawSqlDefinitionInput,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import DerivedColumnTimestampTzInput from './DerivedColumnTimestampTzInput';
import DerivedColumnArrayItemTypeInput from './DerivedColumnArrayItemTypeInput';
import useDerivedColumnDefinitionModel from './useDerivedColumnDefinitionModel';
import DerivedColumnMappedStringEdit from './DerivedColumnMappedStringEdit';
import DerivedColumRawSqlEdit from './DerivedColumRawSqlEdit';
import useDerivedColumnPreviewDataModel from './useDerivedColumnPreviewDataModel';
import DerivedColumnEditPreviewData from './DerivedColumnEditPreviewData';
import DerivedColumnEditActionButtons from './DerivedColumnEditActionButtons';
import { SvgIcon } from '../../../../components/Icon';
import DataModelerDerivedColumnCopyDefinition from '../DataModelerDerivedColumnCopyDefinition';

interface IProps {
  viewKey: string;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
  draftColumn: IDerivedColumnDraft;
  cancel: () => void;
  updateDraft: (props: Partial<IDerivedColumnDraft>) => void;
  confirm: () => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  updateName?: (v: string) => void;
  columnNameError?: string | null;
  setType?: (t: FieldType) => void;
  setTypeParams?: (params: IFieldTypeParamsFieldsFragment) => void;
  isNewDraft?: boolean;
}

export const DERIVED_COLUMN_EDIT_FORM_ANCHOR_ID = 'edit-derived-column-form';

const DerivedColumnEdit = ({
  viewKey,
  tableKey,
  availableColumnsFromServer,
  draftColumn,
  cancel,
  updateDraft,
  confirm,
  updateName,
  setType,
  setTypeParams,
  isNewDraft,
  columnNameError,
  collapsed,
  setCollapsed,
}: IProps) => {
  // Keep track of the last preview request results (data and errors)
  const [previewCachedData, setPreviewCachedData] =
    useState<IPreviewDataRows | null>(null);
  const resetPreviewData = useCallback(() => setPreviewCachedData(null), []);
  const { columnName, fieldType, typeParams, definitionType, definition } =
    draftColumn;

  const handleUpdateName = useCallback(
    (s: string) => {
      if (updateName) {
        updateName(s);
        resetPreviewData();
      }
    },
    [resetPreviewData, updateName],
  );

  const handleSetType = useCallback(
    (t: FieldType) => {
      if (!!setType) {
        setType(t);
        resetPreviewData();
      }
    },
    [resetPreviewData, setType],
  );

  const handleSetTypeParams = useCallback(
    (params: IFieldTypeParamsFieldsFragment) => {
      if (!!setTypeParams) {
        setTypeParams(params);
        resetPreviewData();
      }
    },
    [setTypeParams, resetPreviewData],
  );

  const { canConfirm, setSelectedDefinitionType, rawSqlValidationError } =
    useDerivedColumnDefinitionModel({
      definition,
      definitionType,
      updateDefinition: updateDraft,
      resetPreviewData,
      hasColumnNameError: !!columnNameError,
    });

  const { handlePreviewClick, loading: previewLoading } =
    useDerivedColumnPreviewDataModel({
      availableColumnsFromServer,
      cachedData: previewCachedData,
      draftColumn,
      setCachedData: setPreviewCachedData,
      tableKey,
      viewKey,
    });

  const setMappedStringInput = useCallback(
    (input: IMappedStringDefinitionInput) => {
      updateDraft({
        ...draftColumn,
        definition: {
          mappedString: input,
          rawSql: null,
        },
      });
    },
    [draftColumn, updateDraft],
  );

  const setRawSqlInput = useCallback(
    (input: IRawSqlDefinitionInput) => {
      updateDraft({
        ...draftColumn,
        definition: {
          mappedString: null,
          rawSql: input,
        },
      });
    },
    [draftColumn, updateDraft],
  );

  const onHeaderClick = useCallback(() => {
    if (collapsed) {
      setCollapsed(false);
    }
  }, [collapsed, setCollapsed]);

  const onIconClick = useCallback(() => {
    if (!collapsed) {
      setCollapsed(true);
    }
  }, [collapsed, setCollapsed]);

  return (
    <DerivedColumnDraftFormWrapper>
      <a id={DERIVED_COLUMN_EDIT_FORM_ANCHOR_ID} />
      <UnderTableHeaderDiv $isClickable={collapsed} onClick={onHeaderClick}>
        <SvgIcon
          isHoverable={true}
          name={collapsed ? 'corners-in' : 'corners-out'}
          onClick={onIconClick}
          width={16}
          height={16}
        />
        <div>
          {isNewDraft
            ? 'New Derived Column'
            : `Edit Derived Column: ${columnName}`}
        </div>
        <InformationalTooltip
          tooltipOption={
            isNewDraft
              ? dataModelLabelInfoTooltipOptions.derivedColumn
              : dataModelLabelInfoTooltipOptions.derivedColumnEdit
          }
        />
      </UnderTableHeaderDiv>
      {!collapsed && (
        <>
          <DerivedColumnSelectRow>
            <CustomColumnCell>
              <FieldLabel>Name</FieldLabel>
              <StyledFieldNameInput
                autoComplete="off"
                value={columnName}
                placeholder={'Enter column name'}
                onChange={handleUpdateName}
                disabled={!updateName}
              />
            </CustomColumnCell>
            <CustomColumnCell>
              <FieldLabel>Type</FieldLabel>
              <StyledTypeSelectMenu
                options={FIELD_TYPE_OPTIONS}
                value={fieldType}
                onChange={(v: string) => handleSetType?.(v as FieldType)}
                disabled={!setType}
                placeholder={fieldTypeName(fieldType)}
              />
            </CustomColumnCell>
            <CustomColumnCell>
              {fieldType === FieldType.Timestamp && (
                <DerivedColumnTimestampTzInput
                  manualSelectColumnTypeParams={typeParams}
                  setManualSelectColumnTypeParams={handleSetTypeParams}
                  readOnly={!setTypeParams}
                />
              )}
              {fieldType === FieldType.Array && (
                <DerivedColumnArrayItemTypeInput
                  typeParams={typeParams}
                  setTypeParams={handleSetTypeParams}
                  readOnly={!setTypeParams}
                />
              )}
            </CustomColumnCell>
            {!!columnNameError ? (
              <CustomColumnCell>
                <FieldLabel>Column Name Error</FieldLabel>
                <FieldError type={'custom'} customError={columnNameError} />
              </CustomColumnCell>
            ) : (
              <>
                <CustomColumnCell>
                  <FieldLabel>Definition</FieldLabel>
                  <StyledDefinitionSelectMenu
                    options={[
                      { value: 'RawSql', text: 'SQL' },
                      { value: 'MappedString', text: 'Mapped String' },
                    ]}
                    value={definitionType}
                    placeholder={'SQL / Mapped String'}
                    onChange={(newValue: string) =>
                      setSelectedDefinitionType(
                        newValue as DerivedDefinitionType,
                      )
                    }
                  />
                </CustomColumnCell>
                {isNewDraft && (
                  <DataModelerDerivedColumnCopyDefinition
                    updateDraft={updateDraft}
                  />
                )}
              </>
            )}
          </DerivedColumnSelectRow>
          <DerivedColumnSelectRow>
            {definitionType === 'MappedString' && definition?.mappedString && (
              <DerivedColumnMappedStringEdit
                input={definition.mappedString}
                setInput={setMappedStringInput}
                viewKey={viewKey}
                tableKey={tableKey}
                availableColumnsFromServer={availableColumnsFromServer}
              />
            )}
            {definitionType === 'RawSql' && definition?.rawSql && (
              <DerivedColumRawSqlEdit
                input={definition.rawSql}
                setInput={setRawSqlInput}
                validationError={rawSqlValidationError}
              />
            )}
          </DerivedColumnSelectRow>
          <DerivedColumnSelectRow>
            <DerivedColumnEditActionButtons
              canConfirm={canConfirm}
              onPreviewClick={handlePreviewClick}
              onConfirmClick={confirm}
              onCancel={cancel}
              isNewDraft={isNewDraft ?? false}
            />
          </DerivedColumnSelectRow>
          {definitionType !== null &&
            canConfirm &&
            (previewCachedData || previewLoading) && (
              <CustomColumnCell>
                <DerivedColumnEditPreviewData
                  key={`${tableKey}-${columnName}-${definitionType}`}
                  cachedData={previewCachedData}
                  loading={previewLoading}
                  resetPreviewData={resetPreviewData}
                  loadPreviewData={handlePreviewClick}
                  columnName={columnName}
                />
              </CustomColumnCell>
            )}
        </>
      )}
    </DerivedColumnDraftFormWrapper>
  );
};

export default DerivedColumnEdit;
